import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Empty } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";

import FiltroProductos from "../../../components/FiltroProductos/FiltroProductos";
import { useUser } from "../../../context/UserContext";
import { ListaProductoItemBuscador } from "../../../components/ProductosBuscador/ListaProductoItemBuscador";
import { addToCart } from "../../../utils/productos/productoUtils";
import { getArticulosCerca } from "../../../utils/buscador/buscadorUtils";
import {
  handleFavoriteProductoAction,
  handleFavoriteServicioAction,
} from "../../../utils/favoritos/favoritosUtils";
import { avisarError } from "../../../utils/avisos";
import SkeletonCard from "../../../components/SkeletonCard/SkeletonCard";
import "./Buscador.scss";

export default function BuscadorProductos() {
  const [articulos, setArticulos] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { userLocation, userData } = useUser();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const applyFilters = async (filtrosAFiltrar) => {
    try {
      setLoading(true);
      const articulosEncontrados = await getArticulosCerca(
        userLocation,
        filtrosAFiltrar,
        userData
      );
      setArticulos(articulosEncontrados || []);
      setFilters(filtrosAFiltrar);
    } catch (error) {
      console.error("Error al aplicar filtros y obtener artículos:", error);
      avisarError("No se pudo completar la búsqueda. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const newFilters = {};
      const tipo = searchParams.get("tipo");
      const categoria = searchParams.get("categoria");
      const searchParam = searchParams.get("search");

      if (tipo) newFilters.tipo = tipo;
      if (categoria) newFilters.categoria = categoria;
      if (searchParam) {
        newFilters.search = searchParam;
        setSearch(searchParam);
      } else {
        setSearch("");
      }

      setFilters(newFilters);

      if (userLocation) {
        applyFilters(newFilters);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error al inicializar filtros:", error);
      avisarError("No se pudo completar la búsqueda. Inténtalo de nuevo.");
      setLoading(false);
    }
  }, [searchParams, userLocation]);

  const handleFavoriteToggle = async (producto, isFavorite) => {
    const updatedProducts = articulos.map((item) => {
      if (item.id === producto.id) {
        return { ...item, isFavorite: !isFavorite };
      }
      return item;
    });
    setArticulos(updatedProducts);

    const action = isFavorite ? "remove" : "add";
    if (producto.tipo === "servicio") {
      handleFavoriteServicioAction(producto.id, action, userData);
    } else {
      handleFavoriteProductoAction(producto.id, action, userData);
    }
  };

  const handleAddToCart = (producto) => {
    if (!userData) {
      setIsModalVisible(true);
      return;
    }
    addToCart(producto, userData);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    navigate("/login");
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="buscador-page-wrapper">
      <Toaster richColors position="top-center" />

      <div className="buscador-main-layout">
        {/* Sidebar de filtros */}
        <aside className="buscador-sidebar">
          <FiltroProductos
            inicialFilters={filters}
            applyFilters={applyFilters}
          />
        </aside>

        {/* Contenido de resultados */}
        <main className="buscador-results-content">
          <header className="buscador-results-header">
            {search && search.trim() !== "" && (
              <h1 className="search-title">
                Resultado de búsqueda de: "{search}"
              </h1>
            )}
            <p className="results-count">
              {loading
                ? "Buscando artículos..."
                : `${articulos.length} item(s) encontrados`}
            </p>
          </header>

          <div className="buscador-grid-container">
            {loading ? (
              <Row
                gutter={[
                  { xs: 8, sm: 16, md: 24 },
                  { xs: 12, sm: 24, md: 32 },
                ]}
                justify="start"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((key) => (
                  <Col xs={12} sm={12} md={8} lg={8} xl={6} key={key}>
                    <SkeletonCard />
                  </Col>
                ))}
              </Row>
            ) : articulos.length > 0 ? (
              <Row
                gutter={[
                  { xs: 8, sm: 16, md: 24 },
                  { xs: 12, sm: 24, md: 32 },
                ]}
                justify="start"
              >
                {articulos.map((producto, index) => (
                  <Col
                    xs={12}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={6}
                    key={`${producto.id}-${index}`}
                  >
                    <ListaProductoItemBuscador
                      producto={producto}
                      addToCart={() => handleAddToCart(producto)}
                      favorite={() =>
                        handleFavoriteToggle(producto, producto.isFavorite)
                      }
                      isFavorite={producto.isFavorite}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="empty-results-container">
                <Empty
                  description="No se encontraron artículos con los filtros seleccionados"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      <Modal
        title="Autenticación Requerida"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Iniciar sesión"
        cancelText="Cancelar"
      >
        <p>Debes iniciar sesión para agregar productos al carrito.</p>
      </Modal>
    </div>
  );
}
